﻿import { useState, useRef, useEffect } from 'react';
import DmaFooter from '../DmaFooter';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';
import { ToastProps } from '../../models/toast-props';
import ConfirmationDialog from '../Common/ConfirmationDialog';
import { CrudFunctions } from '../../utilities/crud-functions';
import DocumentDetail from './DocumentDetail';
import { BlobStatus } from "../../models/Blobs/blob-status";
import { BlobMetadata } from '../../models/Blobs/blob-metadata';
import { LookupItem } from '../../models/lookup-item';

import DataGrid, {
    Column,
    FilterRow,
    Selection,
    HeaderFilter,
    SearchPanel,
    Pager,
    Paging,
    Toolbar,
    Item,
    Grouping,
    Scrolling
} from 'devextreme-react/data-grid';
import { LoadPanel, Switch } from 'devextreme-react';
import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import Toast from 'devextreme-react/toast';

export default function ControlledDocuments() {
    const [blobMetadataList, setBlobMetadataList] = useState<BlobMetadata[]>();

    const [categoriesLookup, setCategoriesLookup] = useState<LookupItem[]>();

    const [selectedDoc, setSelectedDoc] = useState<BlobMetadata>(null);

    const [isPopupDirty, setIsPopupDirty] = useState(false);

    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [showFilePreview, setShowFilePreview] = useState<boolean>(true);

    const [globalDialogMetadata, setGlobalDialogMetadata] = useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] = useState<boolean>()

    const [toastProps, setToastProps] = useState<ToastProps>();

    const datagridRef = useRef<any>(null);

    useEffect(() => {
        fetchBlobMetadataList();
        fetchCategoriesLookup();
    }, []);

    const fetchBlobMetadataList = async () => {
        setIsLoading(true);
        try {
            const data = await CrudFunctions.getData(`blobs?isControlled=true`);
            setBlobMetadataList(data);
            return true;
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error Retrieving Blobs Metadata");
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    const fetchCategoriesLookup = async () => {
        try {
            const data = await CrudFunctions.getData("blobs/storage-categories");
            setCategoriesLookup(data.sort((a, b) => a.name.localeCompare(b.name)));
            return true;
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error Retrieving Categories Lookup");
            return false;
        }
    }

    const rowSelectionChanged = (e: any) => {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            let document = structuredClone(e.selectedRowsData[0] as BlobMetadata);
            if (document) {
                setSelectedDoc(document);
            }
        }
    }

    const handleAddDocumentClick = () => {
        // Update state to show the Detail component with a blank template
        clearSelection();
        setSelectedDoc({
            blobId: undefined,
            blobGroupId: undefined,
            versionNumber: 1,
            blobName: undefined,
            fileName: undefined,
            title: undefined,
            controlledDocumentId: undefined,
            description: undefined,
            blobStatusId: BlobStatus.Drafting,
            sizeBytes: null,
            mimeType: undefined
        } as BlobMetadata);
    }

    // callback from DocumentDetail component
    const handleSaveSuccess = (savedDoc: BlobMetadata) => {
        setIsPopupDirty(false);
        setBlobMetadataList(prev => {
            // Check if this doc already exists in our list
            const exists = prev.some(b => b.blobId === savedDoc.blobId);

            if (exists) { // SCENARIO: Update (replace the old one)                
                return prev.map(b => {

                    // Replace the saved document
                    if (b.blobId === savedDoc.blobId) {
                        return savedDoc;
                    }

                    // Handle group-wide updates for the sibling rows
                    if (b.blobGroupId === savedDoc.blobGroupId) {
                        let updatedRow = {
                            ...b,
                            title: savedDoc.title,
                            controlledDocumentId: savedDoc.controlledDocumentId
                        };

                        // Set previous versions to Obsolete if current version was set to Approved
                        // We check version < savedDoc.version to ensure we only touch older ones
                        if (savedDoc.blobStatusId === BlobStatus.Approved && b.versionNumber < savedDoc.versionNumber) {
                            updatedRow.blobStatusId = BlobStatus.Obsolete;
                            updatedRow.blobStatus = BlobStatus[BlobStatus.Obsolete];
                        }
                        return updatedRow;
                    }

                    return b;
                });
            } else { // SCENARIO: New Record (add it to the top)                 
                return [savedDoc, ...prev];
            }
        });

        // Keep the modal focused on the newly saved document 
        setSelectedDoc(savedDoc);
    }

    const handleDiscardConfirm = () => {
        setShowDiscardConfirm(false);
        closePopup();
    }

    const handleCancelRequest = () => {
        if (isPopupDirty) {
            setShowDiscardConfirm(true);
        } else {
            closePopup();
        }
    }

    const closePopup = () => {
        clearSelection();
        setSelectedDoc(null);
        setIsPopupDirty(false);
    }

    const styleGroupRow = (e) => {
        if (e.rowType === "group" && e.groupIndex === 0) { // Top-level group
            e.rowElement.classList.add('controlled-documents-datagrid-group-level-0');
        }
    }

    const clearSelection = () => {
        const gridInstance = datagridRef.current?.instance();
        if (gridInstance) {
            gridInstance.deselectAll();
        } else if (datagridRef.current?.deselectAll) {
            // Fallback for React versions where instance is mapped directly
            datagridRef.current.deselectAll();
        }
    }

    const showMessage = (res: any, mode: ModalMessageType, title: string) => {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    const showToast = (message: string, toastType: string) => {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    const onToastHiding = () => {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    return (
        <div className="pw-content">
            <LoadPanel visible={isLoading} />
            <div id="controlled-documents-container">
                <div className="lq-title-bar">
                    <div className="lq-main-title">Controlled Documents</div>
                </div>

                <div className="pw-comp-row">
                    <Switch
                        value={showFilePreview}
                        onValueChanged={(e) => setShowFilePreview(e.value)}
                        width={50}
                    />
                    <span className="pw-form-unit-label pw-margin-top-02">Show File Previews (disable for slower connections)</span>
                </div>

                <div id="controlled-documents-datagrid-container" className="pw-datagrid">
                    <DataGrid id="documents-datagrid"
                        data-testid="documents-datagrid"
                        ref={datagridRef}
                        dataSource={blobMetadataList ?? []}
                        keyExpr="blobId"
                        onSelectionChanged={rowSelectionChanged}
                        height="100%"
                        showRowLines={true}
                        showBorders={true}
                        repaintChangesOnly={true}
                        onRowPrepared={(e) => { if (e.rowType === "group") styleGroupRow(e) }}
                    >

                        <Grouping autoExpandAll={true} />
                        <SearchPanel visible={true}
                            placeholder="Search..." />
                        <Selection mode={'single'} />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <Scrolling mode="virtual" rowRenderingMode='virtual' />
                        <Paging enabled={false} />
                        <Pager
                            showPageSizeSelector={true}
                            allowedPageSizes={[30, 60, 100]}
                            showInfo={true} />

                        <Column dataField='storageCategory' caption="Category" dataType="string" groupIndex={0} />
                        <Column dataField='blobGroupId' visible={false} groupIndex={1}
                            groupCellRender={({ data }) => {
                                const row = data?.items?.[0];
                                if (!row) return null;
                                return (
                                    <span>Title: {row.title} — Doc Id: {row.controlledDocumentId}</span>
                                );
                            }} />
                        <Column dataField='title' visible={false} />
                        <Column dataField='controlledDocumentId' visible={false} />
                        <Column dataField='versionNumber' caption="Version" dataType="number" width="8%" />
                        <Column dataField='blobStatus' caption="Status" dataType="string" width="10%" />
                        <Column dataField='description' caption="Description" dataType="string" />
                        <Column dataField='fileName' caption="File Name" dataType="string" width="20%" />
                        <Column dataField='createTimestamp' caption="Created" dataType="datetime" format="yyyy-MM-dd HH:mm:ss" width="15%" />

                        <Toolbar>
                            <Item location="before">
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">All Documents</div>
                                </div>
                            </Item>
                            <Item name="searchPanel" location='after' />
                            <Item location='after'>
                                <Button id="addDocumentButton" type='normal' hint='Upload new document' icon='plus'
                                    width={'4em'}
                                    onClick={handleAddDocumentClick} />
                            </Item>
                        </Toolbar>
                    </DataGrid>
                </div>
            </div>

            {selectedDoc &&
                <DialogPopup
                    visible={!!selectedDoc}
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    onHiding={(e) => {
                        // Force the browser to shift focus away from the input field
                        // This solves issue where discard dialog was not displaying when
                        // user goes immediately from typing to dialog's exit button
                        if (document.activeElement instanceof HTMLElement) {
                            document.activeElement.blur();
                        }
                        if (isPopupDirty) {
                            e.cancel = true;
                            setShowDiscardConfirm(true);
                        }
                    }}
                    onHidden={closePopup}
                    showTitle={true}
                    title=""
                    width='100%'
                    height='100%'
                >
                    <DocumentDetail
                        key={selectedDoc?.blobId || 'new'}
                        blobMetadata={selectedDoc}
                        categoriesLookup={categoriesLookup}
                        showFilePreview={showFilePreview}
                        onDirtyStatusChange={setIsPopupDirty}
                        onSaveSuccess={handleSaveSuccess}
                        onCancelRequest={handleCancelRequest}
                        showToast={showToast}
                    />
                </DialogPopup>
            }

            {/* Ask if user wants to discard unsaved changes */}
            <ConfirmationDialog
                visible={showDiscardConfirm}
                title="Discard Changes?"
                message="You have unsaved changes. Are you sure you want to discard them?"
                onHiding={() => setShowDiscardConfirm(false)}
                primaryActionText='Keep Editing'
                primaryActionIcon="edit"
                onPrimaryAction={() => setShowDiscardConfirm(false)}
                secondaryActionText='Discard'
                secondaryActionIcon='trash'
                onSecondaryAction={handleDiscardConfirm}
            />

            <DmaFooter />

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={2000} />
            }

        </div>
    );
}